/**
 * usePolicy — the SINGLE source of truth for booking-policy numbers
 * on the frontend.
 *
 * Operator decision (May 14, 2026): payment due window (72h) and
 * cancellation lock window (24h) are SEPARATE rules. No component
 * may hard-code either value — everyone reads from this hook.
 *
 * Backend canonical endpoint: GET /api/payments/policy returns
 *   {
 *     payment_due_hours,         // default 72
 *     cancellation_window_hours, // default 24
 *     refund_mode,
 *     no_show_policy,
 *     terms_version,
 *   }
 *
 * Hook returns the same shape plus `loading`. Safe defaults are used
 * until the fetch completes — they MUST match the backend defaults
 * so the UI doesn't flash a stale value.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export const POLICY_DEFAULTS = {
  payment_due_hours: 72,
  cancellation_window_hours: 24,
  refund_mode: "learner_choice",
  no_show_policy: "Lesson fee forfeited in full.",
  terms_version: "3.1",
};

let cached = null;
let inflight = null;

export default function usePolicy() {
  const [policy, setPolicy] = useState(cached || POLICY_DEFAULTS);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) return;
    let alive = true;
    if (!inflight) {
      inflight = axios
        .get(`${API}/api/lesson-payments/policy`)
        .then((r) => {
          cached = { ...POLICY_DEFAULTS, ...(r.data || {}) };
          return cached;
        })
        .catch(() => {
          cached = POLICY_DEFAULTS;
          return cached;
        })
        .finally(() => { inflight = null; });
    }
    inflight.then((p) => {
      if (alive) {
        setPolicy(p);
        setLoading(false);
      }
    });
    return () => { alive = false; };
  }, []);

  return { ...policy, loading };
}
